import { Plus, X } from 'lucide-react';
import { useState } from 'react';

import { ApiError } from '../api/client.js';
import { useCreateGroup, useGroups } from '../api/groups.js';
import { PageHeader } from '../components/page-header.js';

import { GroupCreateForm } from './group-create-form.js';
import { GroupRow } from './group-row.js';

export function GroupManager(): JSX.Element {
  const groups = useGroups();
  const [showForm, setShowForm] = useState(false);
  const create = useCreateGroup(() => setShowForm(false));

  if (groups.isLoading) {
    return <p className="p-8 text-text-muted">Loading groups…</p>;
  }
  if (groups.error) {
    return (
      <p className="m-8 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
        Failed to load groups: {String(groups.error)}
      </p>
    );
  }
  const items = groups.data ?? [];

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-8">
      <PageHeader
        eyebrow="Cross-repo"
        title="Groups"
        subtitle="Fan out queries across multiple repositories."
        actions={
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-accent to-accent-dim px-3.5 py-2 text-sm font-semibold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? 'Cancel' : 'New Group'}
          </button>
        }
      />

      {showForm && (
        <GroupCreateForm
          submitting={create.isPending}
          error={formatCreateError(create.error)}
          onSubmit={(body) => {
            create.mutate(body);
          }}
        />
      )}

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="grid gap-3">
          {items.map((g) => (
            <GroupRow key={g.name} group={g} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="grid place-items-center rounded-2xl border border-dashed border-border-subtle bg-surface/40 px-6 py-12 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-xl border border-border-subtle bg-deep text-node-folder">
        <Plus className="h-5 w-5" />
      </div>
      <p className="mt-3 text-sm text-text-muted">No groups yet. Create one to fan out queries.</p>
    </div>
  );
}

function formatCreateError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; missing?: string[] } | null;
    if (body?.error === 'unknown_members' && body.missing?.length) {
      return `Unknown repo slugs: ${body.missing.join(', ')}`;
    }
    if (body?.error) return body.error;
  }
  if (err instanceof Error) return err.message;
  return typeof err === 'string' ? err : 'Unknown error';
}
